import React, { useState, useMemo } from 'react';
import { FaChevronDown, FaUser, FaEnvelope, FaPhone, FaArrowLeft, FaCalendarAlt, FaBed, FaWhatsapp } from 'react-icons/fa';
import ThankYouModal from '../ThankYouModal';
import { db } from '../../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

const ItineraryBookingCard = ({
  pricingCategories,
  lowestPriceIndex,
  selectedPricing,
  handlePricingSelect,
  originalPrice,
  discountPercent,
  formatPrice,
  tourBatches,
  activeMonth,
  visibleMonths,
  hiddenMonths,
  handleMonthSelect,
  getShortMonth,
  isMoreDropdownOpen,
  setIsMoreDropdownOpen,
  selectedBatch,
  handleBatchSelect,
  tripTitle,
  tripDuration // Add tripDuration prop
}) => {
  // Define the WhatsApp number directly in the component
  const whatsappNumber = "919220424206";
  
  // Add state to manage form steps
  const [currentStep, setCurrentStep] = useState(pricingCategories.length > 0 ? 'selection' : 'contact');
  const [contactDetails, setContactDetails] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    personCount: '1' 
  });
  const [errors, setErrors] = useState({});

  // Firestore saving state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Add a ref for the hidden form
  const formRef = React.useRef(null);
  // Add a ref for the hidden iframe
  const iframeRef = React.useRef(null);

  // Helper function to sort months chronologically
  const getMonthSortValue = (monthString) => {
    const [month, year] = monthString.split(' ');
    const monthIndex = new Date(`${month} 1, 2000`).getMonth();
    return (parseInt(year) * 12) + monthIndex;
  };

  // Helper function to check if batch date is in the past
  const isDateInPast = (monthString, dateRange) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to beginning of day
    
    const [month, year] = monthString.split(' ');
    const startDay = parseInt(dateRange.split('-')[0]);
    
    // Create date object for the start of the batch
    const batchDate = new Date(`${month} ${startDay}, ${year}`);
    
    return batchDate < today;
  };
  
  // Filter and sort batch data to only include future dates
  const { filteredBatches, availableMonths } = useMemo(() => {
    const filtered = {};
    
    // Filter out past dates for each month
    Object.entries(tourBatches).forEach(([month, dates]) => {
      const futureDates = dates.filter(date => !isDateInPast(month, date));
      
      // Only include the month if it has future dates
      if (futureDates.length > 0) {
        filtered[month] = futureDates;
      }
    });
    
    // Get sorted list of months with future dates
    const availMonths = Object.keys(filtered).sort((a, b) => 
      getMonthSortValue(a) - getMonthSortValue(b)
    );
    
    return { filteredBatches: filtered, availableMonths: availMonths };
  }, [tourBatches]);
  
  // Recalculate visible and hidden months based on available future dates
  const { sortedVisibleMonths, sortedHiddenMonths } = useMemo(() => {
    const filteredVisible = visibleMonths
      .filter(month => availableMonths.includes(month))
      .sort((a, b) => getMonthSortValue(a) - getMonthSortValue(b));
    
    const filteredHidden = hiddenMonths
      .filter(month => availableMonths.includes(month))
      .sort((a, b) => getMonthSortValue(a) - getMonthSortValue(b));
    
    return { sortedVisibleMonths: filteredVisible, sortedHiddenMonths: filteredHidden };
  }, [availableMonths, visibleMonths, hiddenMonths]);
  
  // Set a valid active month if the current one has no future dates
  React.useEffect(() => {
    if (availableMonths.length > 0 && !availableMonths.includes(activeMonth)) {
      handleMonthSelect(availableMonths[0]);
    }
  }, [availableMonths, activeMonth, handleMonthSelect]);

  // Handle contact form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactDetails(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate the form fields
  const validateForm = () => {
    const newErrors = {};
    if (!contactDetails.name.trim()) newErrors.name = 'Name is required';
    if (!contactDetails.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(contactDetails.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!contactDetails.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(contactDetails.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = 'Phone number must be 10 digits';
    }
    
    // Validate person count
    const personCount = parseInt(contactDetails.personCount);
    if (isNaN(personCount) || personCount < 1) {
      newErrors.personCount = 'Please enter a valid number of persons';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step (from selection to contact form)
  const handleNextStep = () => {
    setCurrentStep('contact');
  };

  // Handle going back to selection step
  const handleBackToSelection = () => {
    setCurrentStep('selection');
  };

  // Handle final form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError('');
    try {
      // Format the batch date to include month and year
      const formattedBatchDate = `${selectedBatch} ${activeMonth}`;
      
      // Prepare booking data
      const bookingData = {
        pricingType: pricingCategories[selectedPricing].type,
        price: pricingCategories[selectedPricing].price,
        batchDate: formattedBatchDate,
        tripTitle,
        tripDuration,
        contactInfo: contactDetails, // contactDetails includes personCount
        timestamp: serverTimestamp(),
        status: 'New', // Save status in the database
      };

      // Get Month Year string for Firestore path
      const now = new Date();
      const month = now.toLocaleString('default', { month: 'long' });
      const year = now.getFullYear();
      const monthYear = `${month},${year}`; // Use comma format to match Enquiry.js

      // Generate unique enquiry ID
      const enquiryId = `enquiry_${now.getTime()}`;

      // Reference the month-year document directly in the Enquiries collection
      const monthYearDocRef = doc(db, 'Enquiries', monthYear);
      
      // Store the enquiry as a map entry using the enquiry ID as the key
      await setDoc(
        monthYearDocRef, 
        { [`entries.${enquiryId}`]: bookingData },
        { merge: true }
      );

      // Send email via formsubmit.co (submit to hidden iframe)
      if (formRef.current) {
        formRef.current.submit();
      }

      // Change to confirmation step
      setCurrentStep('confirmation');
    } catch (error) {
      setSubmitError('Failed to submit enquiry. Please try again.');
      // console.error('Firestore save error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle WhatsApp redirect with pre-filled message
  const handleWhatsappRedirect = () => {
    // Format the batch date to include month and year
    const formattedBatchDate = selectedBatch ? `${selectedBatch} ${activeMonth}` : "Not selected";

    // Format the WhatsApp message with tour details
    const message = encodeURIComponent(
      `Hi! I'm interested in booking the *${tripTitle}* tour.\n\n` +
      `Duration: *${tripDuration}*\n` + 
      `Accommodation: *${pricingCategories[selectedPricing].type}*\n` +
      `Price: ₹${formatPrice(pricingCategories[selectedPricing].price)}\n` +
      `Batch Date: *${formattedBatchDate}*\n\n` +
      'Please provide more information. Thank you!'
    );

    // Generate WhatsApp API URL
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${message}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappUrl, '_blank');
  };

  const handleCloseThankYou = () => {
    // Reset form and go back to selection step
    setContactDetails({
      name: '',
      email: '',
      phone: '',
      message: '',
      personCount: '1'
    });
    setCurrentStep('selection');
  };

  return (
    <div className="booking-card">
      {/* Hidden iframe to prevent navigation on formsubmit */}
      <iframe
        ref={iframeRef}
        name="hidden-formsubmit-frame"
        style={{ display: 'none' }}
        title="hidden-formsubmit-frame"
      />
      {/* Hidden form for formsubmit.co */}
      <form
        ref={formRef}
        action="https://formsubmit.co/swayourwayleads@gmail.com "
        method="POST"
        target="hidden-formsubmit-frame"
        style={{ display: 'none' }}
      >
        <input type="hidden" name="_template" value="table" />
        <input type="hidden" name="Trip Title" value={tripTitle} />
        <input type="hidden" name="Trip Duration" value={tripDuration} />
        <input type="hidden" name="Accommodation" value={pricingCategories.length > 0 ? pricingCategories[selectedPricing].type : 'N/A'} />
        <input type="hidden" name="Price" value={pricingCategories.length > 0 ? formatPrice(pricingCategories[selectedPricing].price) : 'N/A'} />
        <input type="hidden" name="Batch Date" value={selectedBatch ? `${selectedBatch} ${activeMonth}` : 'N/A'} />
        <input type="hidden" name="Name" value={contactDetails.name} />
        <input type="hidden" name="Email" value={contactDetails.email} />
        <input type="hidden" name="Phone" value={contactDetails.phone} />
        <input type="hidden" name="No. of Persons" value={contactDetails.personCount} />
        <input type="hidden" name="Message" value={contactDetails.message} />
        <input type="hidden" name="_captcha" value="false" />
        {/* <input type="hidden" name="_next" value="https://yourdomain.com/thank-you" /> */}
      </form>

      {/* Only show the price header when not in confirmation step and when pricing categories exist */}
      {currentStep !== 'confirmation' && pricingCategories.length > 0 && (
        <div className="booking-price-header">
          <div className="starting-from">
            STARTING FROM
            {selectedPricing === lowestPriceIndex && <span className="best-value-tag">BEST VALUE</span>}
          </div>
          <div className="price-container">
            {/* Calculate slashed price as selected price + 3000 */}
            <span className="original-price">₹{formatPrice(pricingCategories[selectedPricing].price + 3000)}</span>
            <span className="current-price">₹{formatPrice(pricingCategories[selectedPricing].price)}</span>
            <div className="price-chips">
              <span className="price-chip person-chip">Per Person</span>
              <span className="price-chip discount-chip">Up to {discountPercent}% Off</span>
            </div>
          </div>
        </div>
      )}

      {currentStep === 'selection' && pricingCategories.length > 0 ? (
        <>
          <div className="booking-content-section">
            <h4>Tour Pricing - Accomodation</h4>
            <div className="pricing-table">
              {pricingCategories.map((category, index) => (
                <div
                  key={index}
                  className={`pricing-row ${category.discount ? 'discounted' : ''} ${selectedPricing === index ? 'selected' : ''}`}
                  onClick={() => handlePricingSelect(index)}
                >
                  <span className="pricing-type" style={{ textAlign: 'left', marginRight: '10px' }}>{category.type}</span>
                  <span className="pricing-value">₹{formatPrice(category.price)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="booking-content-section">
            <h4>Available Batches</h4>
            {availableMonths.length > 0 ? (
              <div className="batch-selector">
                <div className="month-tabs">
                  {sortedVisibleMonths.map((month, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`month-tab${activeMonth === month ? ' active' : ''}`}
                      onClick={() => handleMonthSelect(month)}
                    >
                      {getShortMonth(month)}
                    </button>
                  ))}
                  {sortedHiddenMonths.length > 0 && (
                    <div className="month-tab-dropdown-container">
                      <button
                        type="button"
                        className={`month-tab more-tab${sortedHiddenMonths.includes(activeMonth) ? ' active' : ''}`}
                        onClick={() => setIsMoreDropdownOpen((v) => !v)}
                        aria-haspopup="listbox"
                        aria-expanded={isMoreDropdownOpen}
                      >
                        More <FaChevronDown className={`dropdown-arrow${isMoreDropdownOpen ? ' open' : ''}`} />
                      </button>
                      {isMoreDropdownOpen && (
                        <div className="month-dropdown-menu" role="listbox">
                          {sortedHiddenMonths.map((month, index) => (
                            <button
                              key={index}
                              type="button"
                              className={`dropdown-item${activeMonth === month ? ' active' : ''}`}
                              onClick={() => handleMonthSelect(month)}
                              role="option"
                              aria-selected={activeMonth === month}
                            >
                              {month}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="month-dates-container">
                  {availableMonths.map((month, index) => (
                    <div
                      key={index}
                      className={`month-dates${activeMonth === month ? ' active' : ''}`}
                      style={{ display: activeMonth === month ? 'flex' : 'none' }}
                    >
                      {filteredBatches[month].map((date, dateIndex) => (
                        <div
                          key={dateIndex}
                          className={`batch-date ${selectedBatch === date ? 'selected' : ''}`}
                          onClick={() => handleBatchSelect(date)}
                        >
                          {date}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-batches-message">
                Please Click on Send Enquiry to proceed.
              </div>
            )}
          </div>

          <div className="booking-actions">
            <button className="booking-btn whatsapp" onClick={handleWhatsappRedirect}>
              <FaWhatsapp className="whatsapp-icon" /> Chat on WhatsApp
            </button>
            <button className="booking-btn primary" onClick={handleNextStep}>Send Query</button>
          </div>
        </>
      ) : currentStep === 'contact' ? (
        <div className="booking-contact-form compact">
          <div className="booking-content-section">
            {pricingCategories.length > 0 && (
              <>
                <div className="back-button-row">
                  <button className="back-button" onClick={handleBackToSelection}>
                    <FaArrowLeft /> Back
                  </button>
                </div>
                
                {/* Booking Summary Section - Combined row for occupancy and dates */}
                <div className="booking-summary">
                  <h4>Booking Summary</h4>
                  <div className="summary-details">
                    <div className="summary-item">
                      <FaBed className="summary-icon" />
                      <span>{pricingCategories[selectedPricing].type}</span>
                      {selectedBatch && (
                        <>
                          <span className="summary-separator">•</span>
                          <FaCalendarAlt className="summary-icon" />
                          <span>{selectedBatch}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
            
            <h4>Your Contact Details</h4>
            
            <div className="form-row">
              <div className="form-group three-quarter">
                <label htmlFor="name">Full Name</label>
                <div className="input-with-icon">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={contactDetails.name}
                    onChange={handleInputChange}
                    placeholder="Your Full Name"
                    className={errors.name ? 'error' : ''}
                    style={{ paddingRight: '2.2rem', paddingLeft: '0.8rem' }}
                  />
                  <FaUser className="input-icon" style={{ left: 'auto', right: '0.8rem' }} />
                </div>
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>
              
              <div className="form-group quarter">
                <label htmlFor="personCount">No. of Persons</label>
                <div className="input-with-icon">
                  <input
                    type="number"
                    id="personCount"
                    name="personCount"
                    min="1"
                    value={contactDetails.personCount}
                    onChange={handleInputChange}
                    placeholder="Count"
                    className={errors.personCount ? 'error' : ''}
                  />
                </div>
                {errors.personCount && <span className="error-text">{errors.personCount}</span>}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group half">
                <label htmlFor="email">Email Address</label>
                <div className="input-with-icon">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={contactDetails.email}
                    onChange={handleInputChange}
                    placeholder="Email address"
                    className={errors.email ? 'error' : ''}
                    style={{ paddingRight: '2.2rem', paddingLeft: '0.8rem' }}
                  />
                  <FaEnvelope className="input-icon" style={{ left: 'auto', right: '0.8rem' }} />
                </div>
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
              
              <div className="form-group half">
                <label htmlFor="phone">Phone Number</label>
                <div className="input-with-icon">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={contactDetails.phone}
                    onChange={handleInputChange}
                    placeholder="Phone number"
                    className={errors.phone ? 'error' : ''}
                    style={{ paddingRight: '2.2rem', paddingLeft: '0.8rem' }}
                  />
                  <FaPhone className="input-icon" style={{ left: 'auto', right: '0.8rem' }} />
                </div>
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="message">Message (Optional)</label>
              <textarea
                id="message"
                name="message"
                value={contactDetails.message}
                onChange={handleInputChange}
                placeholder="Any special requests or questions?"
                rows="2"
              ></textarea>
            </div>
          </div>
          
          {submitError && <div className="error-text" style={{ marginBottom: 8 }}>{submitError}</div>}
          <button className="booking-btn primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
          </button>
        </div>
      ) : (
        // Full-width confirmation step
        <div className="booking-confirmation-step" style={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: 1
        }}>
          <ThankYouModal 
            inline={true}
            isOpen={true}
            onClose={handleCloseThankYou}
            userInfo={contactDetails}
          />
        </div>
      )}
    </div>
  );
};

export default ItineraryBookingCard;
